import axios from 'axios';
import { setupRequestCache } from './requestCache';

/**
 * Setup axios interceptors for automatic token injection and error handling
 * This should be called once when the app initializes
 * 
 * @param {Function} getTokenFunction - Function that returns a promise with the token
 */
export const setupAxiosInterceptors = (getTokenFunction) => {
  // Setup request caching and deduplication FIRST (before token interceptor)
  // This ensures duplicate requests are caught before token injection
  setupRequestCache();

  // Request interceptor: Automatically add token to all requests
  axios.interceptors.request.use(
    async (config) => {
      // Skip token injection if URL is external or doesn't need auth
      if (config.url?.startsWith('http') && !config.url?.includes('myfarelanes.com')) {
        return config;
      }

      // Skip if Authorization header is already set
      if (config.headers?.Authorization) {
        return config;
      }

      try {
        const token = await getTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // If token fetch fails, let the request proceed
        // Individual components can handle the 401 error
        console.warn('Failed to get token for request:', error);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle token expiration and retry
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If error is 401 and we haven't retried yet
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to get a fresh token (force refresh)
          const newToken = await getTokenFunction({ forceRefresh: true });
          
          // Update the request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          // If refresh fails, reject with original error
          // The app's error boundary or auth guard will handle it
          return Promise.reject(error);
        }
      }

      // For other errors, just reject normally
      return Promise.reject(error);
    }
  );
};

/**
 * Reset axios interceptors (useful for testing or cleanup)
 */
export const resetAxiosInterceptors = () => {
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();
};
